import { useCallback } from 'react';
import {
	DASHBOARD_CHART_BOARD,
	HOME_DASHBOARD_CHARTS_MAX,
	HOME_DASHBOARD_CHARTS_STORAGE_PREFIX,
	STATISTICS_PERSISTED_CHARTS_MAX,
	STATISTICS_PERSISTED_CHARTS_STORAGE_PREFIX,
} from '../lib/statisticsPersistedCharts';
import {
	useGetHomeDashboardChartsQuery,
	useGetStatisticsDashboardChartsQuery,
	usePostRestoreStatisticsDashboardDefaultChartsMutation,
	usePutHomeDashboardChartsMutation,
	usePutStatisticsDashboardChartsMutation,
} from '../../../../../slices/statisticsApiSlice';
import useDashboardChartsPersistence from './useDashboardChartsPersistence';

export const useStatisticsDashboardCharts = (userId, { enabled = true } = {}) => {
	const { applyServerCharts, ...persistence } = useDashboardChartsPersistence({
		userId,
		enabled,
		refreshMode: 'frozen',
		maxCharts: STATISTICS_PERSISTED_CHARTS_MAX,
		storagePrefix: STATISTICS_PERSISTED_CHARTS_STORAGE_PREFIX,
		board: DASHBOARD_CHART_BOARD.STATISTICS,
		useGetChartsQuery: useGetStatisticsDashboardChartsQuery,
		usePutChartsMutation: usePutStatisticsDashboardChartsMutation,
	});
	const [restoreDefaultsMutation, { isLoading: isRestoringDefaults }] = usePostRestoreStatisticsDashboardDefaultChartsMutation();

	const restoreDefaultCharts = useCallback(async () => {
		if (!userId || !enabled) {
			return { restored: false, reason: 'disabled' };
		}
		try {
			const response = await restoreDefaultsMutation().unwrap();
			const charts = response?.charts ?? [];
			applyServerCharts(charts);
			return { restored: true, charts };
		} catch {
			return { restored: false, reason: 'request-failed' };
		}
	}, [applyServerCharts, enabled, restoreDefaultsMutation, userId]);

	return {
		...persistence,
		restoreDefaultCharts,
		isRestoringDefaults,
	};
};

export const useHomeDashboardCharts = (userId, { enabled = true } = {}) =>
	useDashboardChartsPersistence({
		userId,
		enabled,
		refreshMode: 'live',
		maxCharts: HOME_DASHBOARD_CHARTS_MAX,
		storagePrefix: HOME_DASHBOARD_CHARTS_STORAGE_PREFIX,
		board: DASHBOARD_CHART_BOARD.HOME,
		useGetChartsQuery: useGetHomeDashboardChartsQuery,
		usePutChartsMutation: usePutHomeDashboardChartsMutation,
	});

export default useStatisticsDashboardCharts;
